import { Component } from '@theme/component';
import { debounce, onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 250;
const DEACTIVATE_DELAY = 350;

class HeaderMenu extends Component {
  connectedCallback() {
    super.connectedCallback();
    onDocumentReady(this.#preloadImages);

    // ✅ PATCH iOS : support touch pour les sous-menus
    this.#initTouchSupport();
  }

  #state = {
    activeItem: null,
  };

  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  // ✅ PATCH iOS : détecte les appareils touch et gère le tap sur les items avec sous-menu
  #initTouchSupport = () => {
    if (!('ontouchstart' in window)) return; // desktop uniquement si pas de touch

    this.addEventListener('touchstart', (event) => {
      if (!(event.target instanceof Element)) return;

      const menuItem = event.target.closest('[role="menuitem"]');
      if (!menuItem) return;

      const parentLi = menuItem.closest('li');
      if (!parentLi) return;

      const submenu = parentLi.querySelector('[ref="submenu[]"]');
      if (!submenu) return; // pas de sous-menu → laisser le lien fonctionner normalement

      // Il y a un sous-menu : on intercepte le touch pour toggle
      event.preventDefault();

      const isSameItem = this.#state.activeItem === menuItem;

      // Ferme l'item actif si différent
      if (this.#state.activeItem && !isSameItem) {
        this.#deactivate();
      }

      if (isSameItem) {
        // Deuxième tap → ferme et laisse naviguer
        this.#deactivate();
      } else {
        // Premier tap → ouvre le sous-menu
        this.#state.activeItem = menuItem;
        this.ariaExpanded = 'true';
        menuItem.ariaExpanded = 'true';
        this.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
        this.dispatchEvent(new MegaMenuHoverEvent());
      }
    }, { passive: false });

    // Ferme le sous-menu si on touche en dehors
    document.addEventListener('touchstart', (event) => {
      if (!this.#state.activeItem) return;
      if (this.contains(event.target)) return;
      this.#deactivate();
    }, { passive: true });
  };

  activate = (event) => {
    this.#debouncedDeactivate.cancel();

    if (this.#state.activeItem) {
      this.#activateHandler(event);
    } else {
      this.#debouncedActivateHandler(event);
    }
  };

  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    this.dataset.overflowExpanded = (event.target.slot !== '').toString();

    this.#deactivate();
    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    const submenu = findSubmenu(item);

    if (!submenu) return;

    this.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate(event);
    }
  }

  #debouncedDeactivate = debounce((event) => {
    this.dataset.overflowExpanded = 'false';

    const item = findMenuItem(event.target);

    if (item != this.#state.activeItem) return;

    this.#deactivate(item);
  }, DEACTIVATE_DELAY);

  #deactivate = (item = this.#state.activeItem) => {
    this.style.setProperty('--submenu-height', '0px');

    if (!item) return;

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, this.animationDelay);
  };

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"]')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[role="menuitem"]');
}

function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
